import React, { useEffect, useMemo, useState } from "react";
import { Grid, Menu } from "antd";
import {
  AppstoreOutlined,
  DollarOutlined,
  UndoOutlined,
  LineChartOutlined,
  RiseOutlined,
  PercentageOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  FallOutlined,
  SwapOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import adminApi from "../../services/adminApi";
import { buildPharmacyHeader } from "./reportCommon";
import type { PharmacyHeader } from "../../utils/reportExport";
import OverviewReport from "./reports/OverviewReport";
import CollectionReport from "./reports/CollectionReport";
import RefundReport from "./reports/RefundReport";
import SalesTrendReport from "./reports/SalesTrendReport";
import ProfitReport from "./reports/ProfitReport";
import GstReport from "./reports/GstReport";
import StockReport from "./reports/StockReport";
import StockLedgerReport from "./reports/StockLedgerReport";
import DayClosePanel from "./reports/DayClosePanel";

const { useBreakpoint } = Grid;

const VIEWS = [
  { key: "overview", label: "Overview", icon: <AppstoreOutlined /> },
  { key: "collections", label: "Bill Collection", icon: <DollarOutlined /> },
  { key: "refunds", label: "Refunds", icon: <UndoOutlined /> },
  { key: "sales-trend", label: "Sales Trend", icon: <LineChartOutlined /> },
  { key: "profit", label: "Profit", icon: <RiseOutlined /> },
  { key: "gst", label: "GST Summary", icon: <PercentageOutlined /> },
  { key: "stock", label: "Stock Report", icon: <DatabaseOutlined /> },
  { key: "near-expiry", label: "Near Expiry", icon: <ClockCircleOutlined /> },
  { key: "expired", label: "Expired", icon: <WarningOutlined /> },
  { key: "low-stock", label: "Low Stock", icon: <FallOutlined /> },
  { key: "ledger", label: "Stock Ledger", icon: <SwapOutlined /> },
  { key: "day-close", label: "Day Close", icon: <LockOutlined /> },
];

const VALID_KEYS = new Set(VIEWS.map((v) => v.key));

const Reports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const screens = useBreakpoint();
  const [pharmacy, setPharmacy] = useState<PharmacyHeader>({
    name: "Pharmacy",
  });

  const viewParam = searchParams.get("view") || "overview";
  const view = VALID_KEYS.has(viewParam) ? viewParam : "overview";

  useEffect(() => {
    adminApi
      .getSettings()
      .then((res) => setPharmacy(buildPharmacyHeader(res.data.data)))
      .catch(() => {});
  }, []);

  const setView = (key: string) => {
    setSearchParams({ view: key }, { replace: false });
  };

  const content = useMemo(() => {
    switch (view) {
      case "collections":
        return <CollectionReport pharmacy={pharmacy} />;
      case "refunds":
        return <RefundReport pharmacy={pharmacy} />;
      case "sales-trend":
        return <SalesTrendReport pharmacy={pharmacy} />;
      case "profit":
        return <ProfitReport pharmacy={pharmacy} />;
      case "gst":
        return <GstReport pharmacy={pharmacy} />;
      case "stock":
        return <StockReport key="all" pharmacy={pharmacy} mode="all" />;
      case "near-expiry":
        return <StockReport key="near" pharmacy={pharmacy} mode="near_expiry" />;
      case "expired":
        return <StockReport key="expired" pharmacy={pharmacy} mode="expired" />;
      case "low-stock":
        return <StockReport key="low" pharmacy={pharmacy} mode="low_stock" />;
      case "ledger":
        return <StockLedgerReport pharmacy={pharmacy} />;
      case "day-close":
        return <DayClosePanel />;
      default:
        return <OverviewReport />;
    }
  }, [view, pharmacy]);

  const isNarrow = !screens.lg;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isNarrow ? "column" : "row",
        gap: 10,
        alignItems: "stretch",
      }}
    >
      <div
        className="page-panel"
        style={{
          width: isNarrow ? "100%" : 188,
          flexShrink: 0,
          alignSelf: "flex-start",
          position: isNarrow ? "static" : "sticky",
          top: 8,
          padding: 4,
        }}
      >
        <Menu
          mode={isNarrow ? "horizontal" : "inline"}
          selectedKeys={[view]}
          onClick={({ key }) => setView(key)}
          style={{ border: "none" }}
          items={VIEWS.map((v) => ({
            key: v.key,
            icon: v.icon,
            label: v.label,
          }))}
        />
      </div>

      <div className="page-panel" style={{ flex: 1, minWidth: 0 }}>
        {content}
      </div>
    </div>
  );
};

export default Reports;
